import {
	ArrowLeft,
	Building2,
	ClipboardCheck,
	MapPin,
	Shield,
	Users,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";

import EditOrganizationDrawer from "../components/EditOrganizationDrawer";
import OrganizationStatusModal from "../components/OrganizationStatusModal";
import { getConfigWithToken } from "../config/config";
import {
	getSuperAdminOrganizationById,
	impersonateSuperAdminOrganizationAdminById,
	updateSuperAdminOrganizationStatusById,
} from "../config/url";
import { storeUser } from "../redux/users/usersSlice";
import { isSuperAdmin } from "../utils/accountType";
import classes from "./SuperAdminOrganizationDetails.module.css";

const formatCreatedDate = (value) => {
	if (!value) return "—";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "—";
	return date.toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" });
};

const formatLocation = (city, state) => {
	const parts = [city, state].filter(Boolean);
	return parts.length ? parts.join(", ") : "—";
};

const formatMetric = (value) => Number(value || 0).toLocaleString("en-US");

const SuperAdminOrganizationDetails = () => {
	const { id } = useParams();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const currentUser = useSelector((state) => state?.user);
	const token = currentUser?.user?.token || localStorage.getItem("user_id");
	const canManageOrganization = isSuperAdmin(currentUser?.user);

	const [editDrawerOpen, setEditDrawerOpen] = useState(false);
	const [hasError, setHasError] = useState(false);
	const [isImpersonating, setIsImpersonating] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [isStatusUpdating, setIsStatusUpdating] = useState(false);
	const [organization, setOrganization] = useState(null);
	const [showStatusModal, setShowStatusModal] = useState(false);

	const requestConfig = useMemo(
		() => ({
			headers: {
				Accept: "application/json",
				"Content-Type": "application/json",
				...(token ? getConfigWithToken(token).headers : {}),
			},
		}),
		[token],
	);

	const fetchOrganization = useCallback(async () => {
		setIsLoading(true);
		setHasError(false);
		try {
			const response = await axios.get(getSuperAdminOrganizationById(id), requestConfig);
			setOrganization(response?.data?.data || null);
		} catch {
			setHasError(true);
			setOrganization(null);
		} finally {
			setIsLoading(false);
		}
	}, [id, requestConfig]);

	useEffect(() => {
		fetchOrganization();
	}, [fetchOrganization]);

	const metrics = organization?.metrics || {};
	const isActive = Boolean(organization?.is_active);
	const nextStatusIsActive = !isActive;

	const handleStatusUpdate = async () => {
		if (!organization || !canManageOrganization) return;

		setIsStatusUpdating(true);
		try {
			const response = await axios.patch(
				updateSuperAdminOrganizationStatusById(organization.id),
				{ is_active: nextStatusIsActive },
				requestConfig,
			);

			if (response.data?.success === false) {
				toast.error(response.data?.message || "Failed to update organization status.");
				return;
			}

			toast.success(
				response.data?.message ||
					(nextStatusIsActive ? "Organization activated." : "Organization deactivated."),
			);
			setShowStatusModal(false);
			await fetchOrganization();
		} catch (error) {
			toast.error(error.response?.data?.message || "Failed to update organization status.");
		} finally {
			setIsStatusUpdating(false);
		}
	};

	const handleImpersonate = async () => {
		if (!organization?.canImpersonate || !canManageOrganization) return;

		setIsImpersonating(true);
		try {
			const response = await axios.post(
				impersonateSuperAdminOrganizationAdminById(organization.id),
				{},
				requestConfig,
			);

			if (response.data?.success !== true) {
				toast.error(response.data?.message || "Failed to log in as organization admin.");
				return;
			}

			localStorage.setItem("superAdminReturnToken", token || "");
			localStorage.setItem("superAdminReturnAccountType", localStorage.getItem("accountType") || "admin");
			localStorage.setItem("superAdminReturnUsername", localStorage.getItem("username") || "");
			localStorage.setItem("superAdminReturnOrganizationId", localStorage.getItem("organizationId") || "");

			localStorage.setItem("accountType", response.data.accountType || "user");
			localStorage.setItem("businessId", response.data.businessId || response.data.organizationId || "");
			localStorage.setItem("isFirstLogin", String(response.data.is_first_login ?? false));
			localStorage.setItem("organizationId", response.data.organizationId || "");
			localStorage.setItem("role", JSON.stringify(response.data.role));
			localStorage.setItem("user_id", response.data.token);
			localStorage.setItem("username", response.data.username || "");

			dispatch(storeUser({ user: { ...response.data, accountType: response.data.accountType || "user" } }));
			toast.success(`Logged in as ${organization.name} admin.`);
			navigate("/dashboard");
		} catch (error) {
			toast.error(error.response?.data?.message || "Failed to log in as organization admin.");
		} finally {
			setIsImpersonating(false);
		}
	};

	return (
		<div className={classes.page}>
			<div className={classes.pageHeader}>
				<button
					aria-label="Back to organizations"
					className={classes.backButton}
					onClick={() => navigate("/organizations")}
					type="button">
					<ArrowLeft size={18} strokeWidth={2.25} />
				</button>
				<h1 className={classes.pageTitle}>Organization Profile</h1>
			</div>

			{isLoading ? (
				<p className={classes.loadingState}>Loading organization profile...</p>
			) : hasError || !organization ? (
				<div className={classes.errorBox}>
					<p>Organization could not be loaded.</p>
					<button className={classes.retryButton} onClick={fetchOrganization} type="button">
						Retry
					</button>
				</div>
			) : (
				<>
					<div className={classes.profileCard}>
						<div className={classes.profileTop}>
							<div className={classes.profileMain}>
								<span aria-hidden="true" className={classes.orgIcon}>
									<Building2 size={22} strokeWidth={1.75} />
								</span>
								<div className={classes.profileIdentity}>
									<div className={classes.nameRow}>
										<p className={classes.orgName}>{organization.name || "—"}</p>
										<span
											className={`${classes.statusBadge} ${
												isActive ? classes.statusActive : classes.statusInactive
											}`}>
											{isActive ? "Active" : "Inactive"}
										</span>
									</div>
									<div className={classes.metaBlock}>
										<p className={classes.metaLine}>
											{formatLocation(organization.city, organization.state)}
										</p>
										<p className={classes.metaLine}>
											Created: {formatCreatedDate(organization.created_at)}
										</p>
									</div>
								</div>
							</div>

							{canManageOrganization ? (
								<div className={classes.profileActions}>
									<button
										className={`${classes.deactivateButton} ${!isActive ? classes.activateButton : ""}`}
										disabled={isStatusUpdating}
										onClick={() => setShowStatusModal(true)}
										type="button">
										{isActive ? "Deactivate" : "Activate"}
									</button>
									<button
										className={classes.editButton}
										onClick={() => setEditDrawerOpen(true)}
										type="button">
										Edit
									</button>
									<button
										className={classes.impersonateButton}
										disabled={!organization.canImpersonate || isImpersonating}
										onClick={handleImpersonate}
										type="button">
										<Shield size={15} strokeWidth={2} />
										Log in as this org&apos;s admin
									</button>
								</div>
							) : null}
						</div>
					</div>

					<div className={classes.metricsGrid}>
						<div className={classes.metricCard}>
							<span aria-hidden="true" className={classes.metricIconWrap}>
								<Users size={18} strokeWidth={1.75} />
							</span>
							<div className={classes.metricContent}>
								<p className={classes.metricValue}>{formatMetric(metrics.activeUsers)}</p>
								<p className={classes.metricLabel}>Users</p>
							</div>
						</div>

						<div className={classes.metricCard}>
							<span aria-hidden="true" className={classes.metricIconWrap}>
								<MapPin size={18} strokeWidth={1.75} />
							</span>
							<div className={classes.metricContent}>
								<p className={classes.metricValue}>{formatMetric(metrics.communityVisits)}</p>
								<p className={classes.metricLabel}>Community Visits</p>
							</div>
						</div>

						<div className={classes.metricCard}>
							<span aria-hidden="true" className={classes.metricIconWrap}>
								<ClipboardCheck size={18} strokeWidth={1.75} />
							</span>
							<div className={classes.metricContent}>
								<p className={classes.metricValue}>{formatMetric(metrics.encounterForms)}</p>
								<p className={classes.metricLabel}>Encounter Forms</p>
							</div>
						</div>
					</div>

					<EditOrganizationDrawer
						isOpen={editDrawerOpen}
						onClose={() => setEditDrawerOpen(false)}
						onSuccess={() => fetchOrganization()}
						organization={organization}
					/>

					<OrganizationStatusModal
						isOpen={showStatusModal}
						isSubmitting={isStatusUpdating}
						mode={isActive ? "deactivate" : "activate"}
						onCancel={() => setShowStatusModal(false)}
						onConfirm={handleStatusUpdate}
					/>
				</>
			)}
		</div>
	);
};

export default SuperAdminOrganizationDetails;
